import { useState } from 'react';
import { Link } from 'react-router-dom';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import HauntedMansion from '@/components/art/HauntedMansion';
import { HauntedTVs, HexCluster, SpectralSignal, TripleGlyph } from '@/components/art/LineArt';
import { rows } from '@/shared/data';
import { useT } from '@/shared/i18n';
import { cn } from '@/lib/utils';
import { GLOW, LABEL, WRAP } from '@/components/home/styles';

const { eq, isIn, and } = window.ApperSDK.CoreSDK;

const HERO_LINE = 'Television that refuses to die.';

const VALUES = [
  {
    t: 'Always on',
    d: "Streams that stay alive at 3 a.m. and on game night. Our servers are watched over around the clock, so the picture doesn't flicker out when you need it.",
  },
  {
    t: 'Honest pricing',
    d: 'No hidden fees, no surprise renewals. You choose a term, we email an invoice, you pay how you like. When it runs out, it simply fades away.',
  },
  {
    t: 'Real support',
    d: 'A real person helps you set up your app, your playlist and your guide. Message us and someone answers. Even after midnight.',
  },
];

const FEATURES = [
  {
    title: 'Live Channels',
    art: HauntedTVs,
    pages: [
      "Ghost Cable's original channel lineup runs live 24/7: horror marathons, late-night talk, true-crime, documentaries and more, all produced and programmed in-house.",
      'Channel surf the way you remember. Flip, browse and land on something good without scrolling through endless menus.',
      'New channels materialize regularly. Your subscription picks them up automatically, at no extra cost.',
    ],
  },
  {
    title: 'The Vault',
    art: HexCluster,
    pages: [
      "Missed a broadcast? The on-demand vault keeps our originals stored for whenever you're ready to watch.",
      'Pause, rewind and resume on another screen. Your progress follows you from room to room.',
      'Seasonal collections surface for Halloween, holidays and premiere weeks.',
    ],
  },
  {
    title: 'Picture Quality',
    art: SpectralSignal,
    pages: [
      'Adaptive streaming keeps the picture sharp on fast connections and steady on slow ones. HD across the lineup.',
      "The premium EPG (included with Wraith 6 and 12 month packages) shows what's on now and next, with show art and descriptions.",
      'Works with popular IPTV players. We send your credentials and setup guide as soon as payment is confirmed.',
    ],
  },
];

export function AnnouncementBar() {
  const { data } = useFetch(
    () =>
      sdk
        .table('announcements_c')
        .select(['Name', 'tone_c', 'show_on_c', 'active_c', 'ends_at_c'])
        .where(and(eq('active_c', true), isIn('show_on_c', ['Site', 'Both'])))
        .orderByDesc('CreatedOn')
        .limit(5)
        .fetch()
        .then(rows),
    [],
  );

  const now = Date.now();
  const live = (data ?? []).find((a) => !a.ends_at_c || new Date(a.ends_at_c).getTime() > now);
  if (!live) return null;

  const tone = {
    Info: 'border-primary/30 bg-primary/10 text-primary',
    Promo: 'border-highlight/30 bg-highlight/15 text-highlight',
    Outage: 'border-destructive/30 bg-destructive/10 text-destructive',
  }[live.tone_c];

  return (
    <div className={cn('border-b px-5 py-2.5 text-center text-sm', tone)}>
      <ApperIcon name={live.tone_c === 'Outage' ? 'TriangleAlert' : 'Sparkles'} size={14} className="mr-2 inline" />
      {live.Name}
    </div>
  );
}

export function Hero() {
  const { t } = useT();
  // Languages written without spaces (Chinese) come back as a single word.
  const words = t(HERO_LINE).split(' ');
  return (
    <section className={cn(WRAP, 'relative flex min-h-[88svh] flex-col justify-center pb-16 pt-16 md:pt-10')}>
      <div className="pointer-events-none order-2 mx-auto mt-12 w-full max-w-md md:absolute md:right-[-2%] md:top-1/2 md:mt-0 md:w-[min(600px,50vw)] md:max-w-none md:-translate-y-[42%]">
        <HauntedMansion className="w-full" />
      </div>
      <div className="relative order-1 md:max-w-[56%]">
        <p className={cn(LABEL, 'animate-pulse')}>{t('IPTV · From beyond the cable box')}</p>
        <h1 className="mt-6 text-[clamp(48px,8.5vw,108px)] font-light leading-[0.98] tracking-[-0.02em]">
          {words.map((w, i) => (
            <span key={`${w}-${i}`} className={cn('me-[0.22em] inline-block', i === words.length - 1 && GLOW)}>
              {w}
            </span>
          ))}
        </h1>
        <p className="mt-8 max-w-md text-base leading-7 text-foreground/85">
          {t(
            "Ghost Cable streams our original live channels and on-demand vault to every screen you own. No cable box, no contracts, no exorcism required. Just pick a spirit and we'll haunt your TV within hours.",
          )}
        </p>
        <div className="mt-10 flex flex-wrap items-center gap-6">
          <Button size="lg" asChild>
            <Link to="/#plans">{t('See plans, from $4')}</Link>
          </Button>
          <Link to="/#features" className="text-sm underline-offset-4 hover:text-primary hover:underline">
            {t('Scroll to explore')}
          </Link>
        </div>
      </div>
    </section>
  );
}

export function Values() {
  const { t } = useT();
  return (
    <section id="features" className={cn(WRAP, 'scroll-mt-24 pt-20')}>
      <h2 className="max-w-2xl text-[clamp(24px,3vw,34px)] font-light leading-[1.3]">
        {t(
          'One subscription. Live channels, on-demand originals and a full program guide, streamed straight to your screens.',
        )}
      </h2>
      <div className="mt-16 grid gap-14 md:grid-cols-3">
        {VALUES.map((v, i) => (
          <div key={v.t}>
            <TripleGlyph variant={i} />
            <h3 className="mt-6 text-xl">{t(v.t)}</h3>
            <p className="mt-4 text-[15px] leading-7 text-muted-foreground">{t(v.d)}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function FeatureRow({ feature, index }) {
  const { t } = useT();
  const [page, setPage] = useState(0);
  const Art = feature.art;
  const flip = index % 2 === 1;

  return (
    <div className="grid items-center gap-10 md:grid-cols-2 md:gap-20">
      <div className={cn(flip && 'md:order-2')}>
        <Art className="mx-auto w-full max-w-sm" />
      </div>
      <div>
        <h3 className="text-[clamp(32px,4vw,48px)] font-light leading-tight">{t(feature.title)}</h3>
        <p key={page} className="mt-6 min-h-36 max-w-md text-[15px] leading-7 text-muted-foreground">
          {t(feature.pages[page])}
        </p>
        <div className="mt-2 flex gap-2">
          {feature.pages.map((p, i) => (
            <button
              key={p}
              type="button"
              onClick={() => setPage(i)}
              aria-label={`${t(feature.title)} ${i + 1}`}
              aria-pressed={i === page}
              className={cn(
                'relative h-10 w-10 border-t text-sm transition-colors',
                i === page
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground',
              )}
            >
              {i + 1}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export function Features() {
  return (
    <section className={cn(WRAP, 'space-y-28 pt-32')}>
      {FEATURES.map((f, i) => (
        <FeatureRow key={f.title} feature={f} index={i} />
      ))}
    </section>
  );
}
